import React, { useEffect, useState } from "react";
import api, { formatTRY } from "@/lib/api";
import { Building2, LoaderCircle, ArrowUpDown } from "lucide-react";

export default function HotelsComparisonTab({ dateFrom, dateTo }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ key: "sales", dir: "desc" });

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/reports/hotels-comparison?date_from=${dateFrom}&date_to=${dateTo}`)
            .then((r) => setItems(r.data?.items || []))
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const sorted = [...items].sort((a, b) => {
        const av = a[sort.key] || 0; const bv = b[sort.key] || 0;
        return sort.dir === "desc" ? bv - av : av - bv;
    });

    const toggle = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));

    if (loading) return <div className="p-8 text-center"><LoaderCircle className="h-6 w-6 mx-auto animate-spin text-violet-500" /></div>;

    return (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="tab-hotels-comparison">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-violet-500" />
                <h3 className="text-sm font-bold">Otel Karşılaştırma — {items.length} otel</h3>
                <span className="ml-auto text-[10px] text-slate-500">Kolon başlıklarına tıklayarak sıralayın</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-900/50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        <tr>
                            <th className="text-left px-3 py-2">Otel</th>
                            <Th onClick={() => toggle("bookings")} label="Rez." active={sort.key === "bookings"} dir={sort.dir} />
                            <Th onClick={() => toggle("nights")} label="Gece" active={sort.key === "nights"} dir={sort.dir} />
                            <Th onClick={() => toggle("sales")} label="Ciro" active={sort.key === "sales"} dir={sort.dir} />
                            <Th onClick={() => toggle("cost")} label="Maliyet" active={sort.key === "cost"} dir={sort.dir} />
                            <Th onClick={() => toggle("profit")} label="Kâr" active={sort.key === "profit"} dir={sort.dir} />
                            <Th onClick={() => toggle("margin_pct")} label="Marj%" active={sort.key === "margin_pct"} dir={sort.dir} />
                            <Th onClick={() => toggle("adr")} label="ADR" active={sort.key === "adr"} dir={sort.dir} />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr><td colSpan={8} className="py-12 text-center text-sm text-slate-500">Bu dönemde satış yapan otel yok.</td></tr>
                        ) : sorted.map((h, i) => (
                            <tr key={h.hotel_id || i} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`hcomp-row-${i}`}>
                                <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-100 max-w-[240px] truncate">{h.hotel_name}</td>
                                <td className="px-3 py-2 text-right text-xs">{h.bookings}</td>
                                <td className="px-3 py-2 text-right text-xs">{h.nights}</td>
                                <td className="px-3 py-2 text-right font-bold text-slate-800 dark:text-slate-100">{formatTRY(h.sales)}</td>
                                <td className="px-3 py-2 text-right text-xs text-amber-700 dark:text-amber-400">{formatTRY(h.cost)}</td>
                                <td className="px-3 py-2 text-right font-bold text-emerald-600 dark:text-emerald-400">{formatTRY(h.profit)}</td>
                                <td className="px-3 py-2 text-right text-xs">%{h.margin_pct}</td>
                                <td className="px-3 py-2 text-right text-xs text-sky-600 dark:text-sky-400">{formatTRY(h.adr)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ onClick, label, active, dir }) {
    return (
        <th className="text-right px-3 py-2 cursor-pointer select-none" onClick={onClick}>
            <span className={`inline-flex items-center gap-1 ${active ? "text-violet-600 dark:text-violet-400" : ""}`}>
                {label} <ArrowUpDown className="h-3 w-3 opacity-60" />
                {active && <span className="text-[9px]">{dir === "desc" ? "↓" : "↑"}</span>}
            </span>
        </th>
    );
}
